/**
 * Service API pour les reviews/notes produits
 * Routes /api/reviews/*
 */

import { api, ApiResponse } from '../services/api';
import {
  Review,
  CreateReviewInput,
  UpdateReviewInput,
  ReviewListResponse,
  ArticleReviewStats,
  TopRatedArticle,
} from '../types/review';

export const reviewApi = {
  // ============================================
  // ROUTES PUBLIQUES
  // ============================================

  /**
   * Recuperer les reviews approuvees d'un article
   * GET /api/reviews/article/:articleId
   */
  async getArticleReviews(
    articleId: string,
    params?: { page?: number; pageSize?: number }
  ): Promise<ApiResponse<ReviewListResponse>> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());
    const query = queryParams.toString();
    return api.get<ReviewListResponse>(`/api/reviews/article/${articleId}${query ? `?${query}` : ''}`);
  },

  /**
   * Recuperer les stats de reviews d'un article
   * GET /api/reviews/article/:articleId/stats
   */
  async getArticleReviewStats(articleId: string): Promise<ApiResponse<ArticleReviewStats>> {
    return api.get<ArticleReviewStats>(`/api/reviews/article/${articleId}/stats`);
  },

  /**
   * Voter sur une review (utile/pas utile)
   * POST /api/reviews/:reviewId/vote
   */
  async voteOnReview(reviewId: string, isHelpful: boolean): Promise<ApiResponse<{ message: string; review: Review }>> {
    return api.post<{ message: string; review: Review }>(`/api/reviews/${reviewId}/vote`, { isHelpful });
  },

  // ============================================
  // ROUTES AUTHENTIFIEES
  // ============================================

  /**
   * Creer une nouvelle review
   * POST /api/reviews
   */
  async createReview(input: CreateReviewInput): Promise<ApiResponse<{ message: string; review: Review }>> {
    return api.post<{ message: string; review: Review }>('/api/reviews', input);
  },

  /**
   * Recuperer mes reviews
   * GET /api/reviews/my-reviews
   */
  async getMyReviews(params?: { page?: number; pageSize?: number }): Promise<ApiResponse<ReviewListResponse>> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());
    const query = queryParams.toString();
    return api.get<ReviewListResponse>(`/api/reviews/my-reviews${query ? `?${query}` : ''}`);
  },

  /**
   * Mettre a jour une review
   * PUT /api/reviews/:reviewId
   */
  async updateReview(
    reviewId: string,
    updates: UpdateReviewInput
  ): Promise<ApiResponse<{ message: string; review: Review }>> {
    return api.put<{ message: string; review: Review }>(`/api/reviews/${reviewId}`, updates);
  },

  /**
   * Supprimer une review
   * DELETE /api/reviews/:reviewId
   */
  async deleteReview(reviewId: string): Promise<ApiResponse<{ message: string }>> {
    return api.delete<{ message: string }>(`/api/reviews/${reviewId}`);
  },

  // ============================================
  // ROUTES REPONSE VENDEUR (BackOffice users on FrontOffice)
  // ============================================

  /**
   * Ajouter une reponse vendeur a une review
   * POST /api/reviews/:reviewId/seller-reply
   */
  async addSellerReply(reviewId: string, reply: string): Promise<ApiResponse<{ message: string; review: Review }>> {
    return api.post<{ message: string; review: Review }>(`/api/reviews/${reviewId}/seller-reply`, { reply });
  },

  // ============================================
  // ROUTES ADMIN
  // ============================================

  /**
   * Recuperer les reviews en attente de moderation
   * GET /api/reviews/admin/pending
   */
  async getPendingReviews(params?: { page?: number; pageSize?: number }): Promise<ApiResponse<ReviewListResponse>> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());
    const query = queryParams.toString();
    return api.get<ReviewListResponse>(`/api/reviews/admin/pending${query ? `?${query}` : ''}`);
  },

  /**
   * Recuperer toutes les reviews (avec filtres)
   * GET /api/reviews/admin/all
   */
  async getAllReviews(params?: {
    articleId?: string;
    userId?: string;
    status?: string;
    minRating?: number;
    maxRating?: number;
    page?: number;
    pageSize?: number;
    sortBy?: string;
    sortOrder?: string;
  }): Promise<ApiResponse<ReviewListResponse>> {
    const queryParams = new URLSearchParams();
    if (params?.articleId) queryParams.append('articleId', params.articleId);
    if (params?.userId) queryParams.append('userId', params.userId);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.minRating) queryParams.append('minRating', params.minRating.toString());
    if (params?.maxRating) queryParams.append('maxRating', params.maxRating.toString());
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    const query = queryParams.toString();
    return api.get<ReviewListResponse>(`/api/reviews/admin/all${query ? `?${query}` : ''}`);
  },

  /**
   * Moderer une review
   * POST /api/reviews/admin/:reviewId/moderate
   */
  async moderateReview(
    reviewId: string,
    data: { status: 'approved' | 'rejected'; moderationNotes?: string }
  ): Promise<ApiResponse<{ message: string; review: Review }>> {
    return api.post<{ message: string; review: Review }>(`/api/reviews/admin/${reviewId}/moderate`, data);
  },

  /**
   * Supprimer une review (admin)
   * DELETE /api/reviews/admin/:reviewId
   */
  async adminDeleteReview(reviewId: string): Promise<ApiResponse<{ message: string }>> {
    return api.delete<{ message: string }>(`/api/reviews/admin/${reviewId}`);
  },

  /**
   * Bloquer un utilisateur des commentaires
   * POST /api/reviews/admin/user/:userId/block
   */
  async blockUserFromCommenting(
    userId: string,
    data: { reason?: string; deleteReviews?: boolean }
  ): Promise<ApiResponse<{ message: string; deletedReviewsCount?: number }>> {
    return api.post<{ message: string; deletedReviewsCount?: number }>(`/api/reviews/admin/user/${userId}/block`, data);
  },

  /**
   * Debloquer un utilisateur des commentaires
   * POST /api/reviews/admin/user/:userId/unblock
   */
  async unblockUserFromCommenting(userId: string): Promise<ApiResponse<{ message: string }>> {
    return api.post<{ message: string }>(`/api/reviews/admin/user/${userId}/unblock`, {});
  },

  /**
   * Recuperer le top des articles les mieux notes
   * GET /api/reviews/admin/top-rated
   */
  async getTopRatedArticles(limit?: number): Promise<ApiResponse<TopRatedArticle[]>> {
    const query = limit ? `?limit=${limit}` : '';
    return api.get<TopRatedArticle[]>(`/api/reviews/admin/top-rated${query}`);
  },

  /**
   * Recuperer le classement de tous les articles
   * GET /api/reviews/admin/ranking
   */
  async getArticlesRanking(): Promise<ApiResponse<TopRatedArticle[]>> {
    return api.get<TopRatedArticle[]>('/api/reviews/admin/ranking');
  },

  /**
   * Supprimer la reponse vendeur d'une review (admin)
   * DELETE /api/reviews/:reviewId/seller-reply
   */
  async deleteSellerReply(reviewId: string): Promise<ApiResponse<{ message: string; review: Review }>> {
    return api.delete<{ message: string; review: Review }>(`/api/reviews/${reviewId}/seller-reply`);
  },
};
